import React from 'react';

import { ParsedData, StepProps } from '@betterment/js-runtime';
import { Button, FormikField, Input, Text } from '@betterment/samba-react';
import { Heading } from '@betterment/style-closet-react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { ActivateCardParams } from '../Checking/data';
import { applyApiErrorsToFormik } from '../Checking/form-helpers';
import FlashErrorMessage from '../Shared/components/FlashErrorMessage';
import WorkflowLayout from '../Shared/flows/components/ConnectedWorkflowLayout';

const ActivationSchema = Yup.object().shape({
  cardNumberMasked: Yup.string()
    .required('Please enter the last four digits on your card')
    .matches(/^\d{4}$/, 'Please enter the last four digits on your card'),
  dateOfBirth: Yup.string()
    .nullable()
    .required('Please enter your birth date')
    .matches(/\d{2}\/\d{2}\/\d{4}/, 'Please enter a valid birth date'),
});

const initialValues: ActivateCardParams = {
  cardNumberMasked: '',
  // If we initialize this with `null`, react warns about switching between controlled/uncontrolled
  // inputs. empty string has the same behavior, but it violates our type constraint.
  dateOfBirth: '' as unknown as Date,
};

interface Props extends StepProps {
  onActivateCard: (data: ActivateCardParams) => Promise<ParsedData<{}>>;
  isExpiringCardReplacement: boolean;
}

export const VerifyCard: React.FC<Props> = ({ onActivateCard, goToNextStep, isExpiringCardReplacement }) => {
  const onSubmit = async (values: ActivateCardParams, formik: FormikHelpers<ActivateCardParams>) => {
    formik.setStatus(null);
    let response: ParsedData<{}>;
    try {
      response = await onActivateCard(values);
    } finally {
      formik.setSubmitting(false);
    }

    if (response && response.errors) {
      applyApiErrorsToFormik(formik, response.errors);
    } else {
      goToNextStep();
    }
  };

  return (
    <WorkflowLayout title="Activate your debit card">
      <WorkflowLayout.Slot>
        <Heading as="h1" fontSize={[5, 6]} mb="6">
          Activate your debit card
        </Heading>
        {isExpiringCardReplacement ? (
          <>
            <Text
              as="p"
              css={{
                lineHeight: 'lineHeight.100',
                marginBottom: 'space.200',
              }}
            >
              Enter the last four digits of your debit card number and your birth date. Your debit card number can be
              found on the back of your card.
            </Text>
            <Text
              as="p"
              css={{
                lineHeight: 'lineHeight.100',
                marginBottom: 'space.200',
              }}
            >
              Your PIN will remain what you set for your existing debit card.
            </Text>
          </>
        ) : (
          <Text
            as="p"
            css={{
              lineHeight: 'lineHeight.100',
              marginBottom: 'space.200',
            }}
          >
            Enter the last four digits of your debit card and your birth date to begin using your card for purchases.
            Your debit card number can be found on the back of your card on the bottom row.
          </Text>
        )}
      </WorkflowLayout.Slot>
      <WorkflowLayout.Slot>
        <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={ActivationSchema}>
          {({ isSubmitting }) => {
            const shouldDisableForm = isSubmitting;
            return (
              <Form>
                <FlashErrorMessage name="base" />
                <FormikField name="cardNumberMasked">
                  <FormikField.Label>Last four digits of your card</FormikField.Label>
                  <FormikField.Control>
                    <Input name="cardNumberMasked" maxLength={4} disabled={shouldDisableForm} />
                  </FormikField.Control>
                  <FormikField.ErrorMessage />
                </FormikField>

                <FormikField name="dateOfBirth">
                  <FormikField.Label>Birth date</FormikField.Label>
                  <FormikField.Control>
                    <Input mask="date-mm/dd/yyyy" name="dateOfBirth" disabled={shouldDisableForm} />
                  </FormikField.Control>
                  <FormikField.ErrorMessage />
                </FormikField>

                <Button type="submit" disabled={shouldDisableForm} css={{ mt: 'space.100' }}>
                  Activate card
                </Button>
              </Form>
            );
          }}
        </Formik>
      </WorkflowLayout.Slot>
    </WorkflowLayout>
  );
};
